import '@availity/yup';
import * as yup from 'yup';

const COMMA_SEPARATED_VALUES_REGEXP =
  /^[a-zA-Z\u00C0-\u017F0-9\s'\-./]+(?:(?:,)[a-zA-Z\u00C0-\u017F0-9\s'\-./]+)*[a-zA-Z\u00C0-\u017F0-9\s'\-./]+$/g;

const formValidation = yup.object().shape({
  name: yup.string().required(),
  customerId: yup.string().required(),
  campaignIds: yup.mixed().required(),
  isRCS: yup.boolean(),
  qualified: yup.string().required(),
  leadFormId: yup.string().required(),
  sector: yup.string().required(),
  priority: yup.number().required().min(1).max(10),
  monetizationOrdersAllowed: yup.mixed().required(),
  messageTemplateId: yup.string().required(),
  costNegativeClosure: yup.number().when('qualified', {
    is: 'yes',
    then: () => yup.number().required().min(0),
  }),
  maxCallAttempts: yup.number().when('qualified', {
    is: 'yes',
    then: () => yup.number().required().min(2),
  }),
  phoneExchangeRoute: yup.string().when('qualified', {
    is: 'yes',
    then: () => yup.string().required(),
  }),
  phoneExchangeQueue: yup.string().when('qualified', {
    is: 'yes',
    then: () => yup.string().required(),
  }),
  sendMessageAfterRecall: yup
    .number()
    .min(0)
    .when('maxCallAttempts', (maxCallAttempts, schema) =>
      schema.max(parseInt(maxCallAttempts) - 1),
    ),
  recallMessageTemplateId: yup
    .string()
    .when('sendMessageAfterRecall', (sendMessageAfterRecall, schema) =>
      parseInt(sendMessageAfterRecall) > 0 ? schema.required() : schema,
    ),
  callStopwatch: yup.number().min(0).max(10),
  negativeReasons: yup.string().matches(COMMA_SEPARATED_VALUES_REGEXP, {
    excludeEmptyString: true,
  }),
  negativeWebService: yup.string().when('qualified', {
    is: 'yes',
    then: () => yup.string().required(),
  }),
  negativeMessageTemplateId: yup.string().required(),
  negativeMessageSendDelay: yup.number().when('negativeMessageTemplateId', {
    is: (value) => value && value !== 'none',
    then: () => yup.number().required().min(1),
  }),
  expiredMessageTemplateId: yup.string().required(),
  callMessageTemplateIds: yup.array().of(yup.string()).nullable(),
  localAgencyFinderId: yup.string().nullable(),
  // The sentence the lead reads when the agent hands her to a person. At the
  // root and not inside aiRouting: nothing here needs a sibling of that object,
  // and the engine reads it as context.acquisitionOrder.aiHandoffMessage.
  //
  // Required on every routed order, with the same two conditions routeLead.js
  // uses — and nothing else. It used to also require autonomous mode and two
  // or more offers, because the only use was a proposal spanning several
  // offers. It now also covers the prequalified and maxTurns handovers, which
  // happen in both modes and at any offer count, so both accessory conditions
  // are gone. Fewer conditions, not more: the field is simply needed whenever
  // an agent can take the conversation.
  aiHandoffMessage: yup
    .string()
    .when(['aiRouting.enabled', 'aiRouting.agentId'], {
      is: (enabled, agentId) => enabled === true && !!agentId,
      then: () => yup.string().trim().required(),
    }),
  // How many offers the agent may have accepted together in one conversation.
  // At the root for the mirror image of aiHandoffMessage's reason: this one
  // DOES need a sibling of the root — monetizationOrdersAllowed, for its
  // ceiling — and inside a nested object().shape() a when() on that name
  // would resolve against the nested object and never fire.
  //
  // Optional on purpose: absent means one offer, which is what the engine did
  // before this field existed. Making it required would invalidate every
  // acquisition order already saved.
  //
  // Only the ceiling test against monetizationOrdersAllowed is gated by the
  // .when() below, on a routed order with an agent — that rule is meaningless
  // without a routed order to size the ceiling against. The .integer() and
  // .min(1) above it are type and range sanity, not business rule, so they
  // apply unconditionally: a non-routed order still can't carry a negative,
  // fractional, or zero value here. splitPercentage a few lines below is the
  // same convention — unconditional type/range checks with the conditional
  // business rule layered on top — so this split is deliberate, not an
  // oversight.
  aiMaxOffersPerClosure: yup
    .number()
    .transform((value, original) => (original === '' ? undefined : value))
    .integer()
    .min(1)
    .when(['aiRouting.enabled', 'aiRouting.agentId', 'monetizationOrdersAllowed'], {
      is: (enabled, agentId) => enabled === true && !!agentId,
      then: (schema) =>
        schema.test(
          'within-the-offers-the-order-has',
          'cannot exceed the offers this acquisition order carries',
          function (value) {
            if (value === undefined || value === null) return true;
            const allowed = this.parent.monetizationOrdersAllowed;
            const count = Array.isArray(allowed) ? allowed.length : 0;
            return count > 0 && value <= count;
          },
        ),
    }),
  aiRouting: yup.object().shape({
    enabled: yup.boolean(),
    agentId: yup.string().when('enabled', {
      is: true,
      then: () => yup.string().required(),
    }),
    mode: yup.string().when('enabled', {
      is: true,
      then: () => yup.string().oneOf(['autonomous', 'prequalify']).required(),
    }),
    splitPercentage: yup
      .number()
      .integer()
      .min(0)
      .max(100)
      .when('enabled', {
        is: true,
        then: () => yup.number().integer().min(1).max(100).required(),
      }),
    // The share above is then drawn only among the leads arriving while the
    // call center is closed (routeLead.js). Optional: absent is the routing
    // of before, at any hour, so no saved order needs migrating.
    onlyWhenCallCenterClosed: yup.boolean(),
  }),
});

export default formValidation;
